import React, { useEffect, useState } from 'react';
import type { Bundle } from './types';

interface BundleSelectorProps {
  bundles?: Bundle[];
  onBundleSelect?: (bundle: Bundle) => void;
}

const DEFAULT_BUNDLE_IMAGE = 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&q=80';

/**
 * BundleSelector Component
 * Displays a horizontal scrollable carousel of yoga bundles/series
 */
export const BundleSelector: React.FC<BundleSelectorProps> = ({
  bundles = defaultBundles,
  onBundleSelect,
}) => {
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(bundles[0]?.id || null);

  useEffect(() => {
    if (bundles.length === 0) {
      setSelectedBundleId(null);
      return;
    }

    const currentSelectionExists = selectedBundleId && bundles.some((bundle) => bundle.id === selectedBundleId);
    if (!currentSelectionExists) {
      setSelectedBundleId(bundles[0].id);
    }
  }, [bundles, selectedBundleId]);

  const handleBundleClick = (bundle: Bundle) => {
    setSelectedBundleId(bundle.id);
    onBundleSelect?.(bundle);
  };

  return (
    <section className="mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-5 h-px bg-gray-300" />
          <h3 className="text-xs font-semibold tracking-widest text-gray-600 uppercase">
            Series y Bundles
          </h3>
        </div>
        <a
          href="#"
          className="text-xs font-semibold text-sb-600 uppercase tracking-widest hover:border-b border-sb-600 transition-colors"
        >
          Ver Todos →
        </a>
      </div>

      {/* Bundles Carousel */}
      <div className="overflow-x-auto scrollbar-hide">
        <div className="flex gap-3 pb-2">
          {bundles.map((bundle) => (
            <button
              key={bundle.id}
              onClick={() => handleBundleClick(bundle)}
              className={`flex-shrink-0 w-48 rounded-2xl overflow-hidden transition-all ${
                selectedBundleId === bundle.id
                  ? 'border-2 border-so-600 shadow-xl'
                  : 'border-2 border-transparent hover:shadow-lg hover:border-sb-200'
              }`}
            >
              {/* Thumbnail */}
              <div className="relative h-24 overflow-hidden group">
                <img
                  src={bundle.imageUrl || DEFAULT_BUNDLE_IMAGE}
                  alt={bundle.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  onError={(e) => {
                    const target = e.currentTarget;
                    if (target.src !== DEFAULT_BUNDLE_IMAGE) {
                      target.src = DEFAULT_BUNDLE_IMAGE;
                    }
                  }}
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                {/* Class Count */}
                <span className="absolute bottom-2 left-2 text-xs font-light text-white/90">
                  {bundle.classCount} classes · {bundle.totalDuration}
                </span>

                {/* Check or Lock Icon */}
                {bundle.isPremium ? (
                  <div className="absolute top-2 left-2 w-5 h-5 rounded-full bg-sb-600/90 flex items-center justify-center">
                    <svg
                      width="9"
                      height="10"
                      viewBox="0 0 14 16"
                      fill="none"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    >
                      <rect x="2" y="7" width="10" height="8" rx="2" />
                      <path d="M4 7V5a3 3 0 0 1 6 0v2" />
                    </svg>
                  </div>
                ) : (
                  selectedBundleId === bundle.id && (
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-so-600 flex items-center justify-center shadow-lg">
                      <svg
                        width="10"
                        height="8"
                        viewBox="0 0 10 8"
                        fill="none"
                        stroke="white"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 4L3.8 7L9 1" />
                      </svg>
                    </div>
                  )
                )}
              </div>

              {/* Info */}
              <div className="bg-white p-3">
                <div className="text-xs font-semibold text-sb-600 uppercase tracking-wider mb-1">
                  {bundle.tag || 'Serie'}
                </div>
                <h4 className="text-sm font-medium text-gray-900 leading-tight mb-2 text-left line-clamp-2">
                  {bundle.title}
                </h4>

                {bundle.instructor && (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-600">{bundle.instructor.name}</span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

const defaultBundles: Bundle[] = [
  {
    id: 'bundle-1',
    title: 'Rise & Radiate — 7-Day Flow',
    tag: 'Morning Series',
    imageUrl: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&q=80',
    classCount: 8,
    totalDuration: '3h 20m',
    instructor: { name: 'Sofia Arenas', initials: 'SA' },
    isSelected: true,
  },
  {
    id: 'bundle-2',
    title: 'Moon Cycle Yoga Program',
    tag: 'Lunar Journey',
    imageUrl: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&q=80',
    classCount: 12,
    totalDuration: '6h 10m',
    instructor: { name: 'Luna Vásquez', initials: 'LV' },
  },
  {
    id: 'bundle-3',
    title: 'Sacred Breath Initiation',
    tag: 'Breathwork · Premium',
    imageUrl: 'https://images.unsplash.com/photo-1599447421416-3414500d18a5?w=400&q=80',
    classCount: 6,
    totalDuration: '2h 45m',
    isPremium: true,
  },
  {
    id: 'bundle-4',
    title: 'Deep Rest — Yin & Nidra',
    tag: 'Restorative',
    imageUrl: 'https://images.unsplash.com/photo-1575052814086-f385e2e2ad1b?w=400&q=80',
    classCount: 5,
    totalDuration: '1h 55m',
    instructor: { name: 'Clara Espinoza', initials: 'CE' },
  },
  {
    id: 'bundle-5',
    title: 'Sovereign Strength 30-Day',
    tag: 'Vinyasa · Premium',
    imageUrl: 'https://images.unsplash.com/photo-1611072172377-0cabc1fbc24f?w=400&q=80',
    classCount: 10,
    totalDuration: '5h 30m',
    isPremium: true,
  },
];

export default BundleSelector;
